<template>
  <div class="ucp">
    <!-- ========== 工具列：月份切換 + 草稿狀態 + 儲存 ========== -->
    <div class="ucp-toolbar">
      <div class="ucp-month">
        <button class="ucp-nav" @click="prevMonth" aria-label="上個月">‹</button>
        <span class="ucp-month-label">{{ year }} 年 {{ month }} 月</span>
        <button class="ucp-nav" @click="nextMonth" aria-label="下個月">›</button>
        <button class="ucp-today" @click="goToday">今天</button>
      </div>

      <div class="ucp-actions">
        <span class="ucp-dirty" :class="{ active: dirtyCount > 0 }">
          {{ dirtyCount > 0 ? `已修改 ${dirtyCount} 格（未儲存）` : "沒有未儲存的變更" }}
        </span>
        <button
          class="ucp-btn secondary"
          :disabled="dirtyCount === 0"
          @click="discard"
        >
          放棄變更
        </button>
        <button
          class="ucp-btn primary"
          :disabled="dirtyCount === 0"
          @click="save"
        >
          儲存
        </button>
      </div>
    </div>

    <p class="ucp-hint">
      點時段切換「開放 / 不開放」；點日期數字可整天一起切換。可以跨月份修改，最後按一次「儲存」。
    </p>

    <!-- ========== 圖例 ========== -->
    <div class="ucp-legend">
      <span><i class="dot open"></i>開放</span>
      <span><i class="dot blocked"></i>不開放</span>
      <span><i class="dot booked"></i>已預約</span>
      <span><i class="dot changed"></i>未儲存</span>
    </div>

    <!-- ========== 月曆 ========== -->
    <div class="ucp-grid">
      <div v-for="w in WEEKDAYS" :key="'h' + w" class="ucp-weekday">
        {{ w }}
      </div>

      <template v-for="(cell, i) in cells" :key="i">
        <div v-if="!cell" class="ucp-cell ucp-blank"></div>

        <div
          v-else
          class="ucp-cell"
          :class="{
            'is-past': cell.isPast,
            'is-today': cell.dateStr === todayStr,
          }"
        >
          <button
            class="ucp-date"
            :disabled="cell.isPast"
            :title="cell.isPast ? '' : '點一下：整天開放 / 不開放'"
            @click="toggleDay(cell.dateStr)"
          >
            <span class="ucp-date-num">{{ cell.day }}</span>
            <span class="ucp-date-wd">（{{ WEEKDAYS[cell.weekday] }}）</span>
            <span v-if="isDayFullyBlocked(cell.dateStr)" class="ucp-day-tag">
              整天休
            </span>
          </button>

          <div class="ucp-slots">
            <button
              v-for="slot in allTimeSlots"
              :key="slot"
              class="ucp-slot"
              :class="slotClass(cell.dateStr, slot)"
              :disabled="cell.isPast"
              :title="slotTitle(cell.dateStr, slot)"
              @click="toggleSlot(cell.dateStr, slot)"
            >
              <span class="ucp-slot-time">{{ shortLabel(slot) }}</span>
              <span class="ucp-slot-state">{{
                slotStateText(cell.dateStr, slot)
              }}</span>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- ========== 除錯用：原始資料 ========== -->
    <details class="ucp-debug">
      <summary>查看目前已儲存的原始設定（unavailable.json）</summary>
      <pre>{{ unavailable }}</pre>
    </details>
  </div>
</template>

<script>
const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

/* 台北時間的今天 YYYY-MM-DD（不用 toISOString，避免 00:00~08:00 變成昨天） */
function taipeiTodayStr() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return parts; // en-CA 格式即 YYYY-MM-DD
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export default {
  name: "UnavailableCalendarPanel",

  props: {
    unavailable: { type: Object, required: true }, // { fullDay: [], slots: [{date, timeSlots}] }
    bookings: { type: Array, default: () => [] },
    allTimeSlots: { type: Array, required: true },
    services: { type: Array, default: () => [] },
  },

  emits: ["save", "dirty-change"],

  data() {
    const today = taipeiTodayStr();
    return {
      WEEKDAYS,
      todayStr: today,
      year: Number(today.slice(0, 4)),
      month: Number(today.slice(5, 7)),
      /* 草稿：key = `${date}|${slot}` → true(不開放) / false(開放)；只記跟已儲存不同的格 */
      draft: {},
    };
  },

  computed: {
    dirtyCount() {
      return Object.keys(this.draft).length;
    },

    fullDaySet() {
      return new Set(
        Array.isArray(this.unavailable.fullDay) ? this.unavailable.fullDay : [],
      );
    },

    /* date → Set(已儲存的不開放時段字串，含舊格式) */
    savedSlotsByDate() {
      const map = {};
      const list = Array.isArray(this.unavailable.slots)
        ? this.unavailable.slots
        : [];
      list.forEach((s) => {
        if (!s || !s.date) return;
        if (!map[s.date]) map[s.date] = new Set();
        (Array.isArray(s.timeSlots) ? s.timeSlots : []).forEach((t) =>
          map[s.date].add(t),
        );
      });
      return map;
    },

    /* `${date}|${slot}` → [姓名...]（排除已取消的預約） */
    bookedMap() {
      const map = {};
      this.bookings.forEach((b) => {
        if (!b || !b.date || b.status === "canceled") return;
        const slots = Array.isArray(b.timeSlots)
          ? b.timeSlots
          : b.timeSlot
            ? [b.timeSlot]
            : [];
        slots.forEach((slot) => {
          const key = `${b.date}|${slot}`;
          if (!map[key]) map[key] = [];
          map[key].push(b.name || "（未填姓名）");
        });
      });
      return map;
    },

    /* 月曆格：前面補空格，之後每天一格 */
    cells() {
      const firstWeekday = new Date(this.year, this.month - 1, 1).getDay();
      const daysInMonth = new Date(this.year, this.month, 0).getDate();
      const list = [];
      for (let i = 0; i < firstWeekday; i++) list.push(null);
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${this.year}-${pad2(this.month)}-${pad2(d)}`;
        list.push({
          day: d,
          dateStr,
          weekday: (firstWeekday + d - 1) % 7,
          isPast: dateStr < this.todayStr,
        });
      }
      return list;
    },
  },

  watch: {
    /* 父層儲存成功後會換掉 unavailable 物件 → 清空草稿 */
    unavailable() {
      this.draft = {};
    },
    dirtyCount(n, old) {
      if (n > 0 && old === 0) {
        window.addEventListener("beforeunload", this.onBeforeUnload);
      } else if (n === 0 && old > 0) {
        window.removeEventListener("beforeunload", this.onBeforeUnload);
      }
      if ((n > 0) !== (old > 0)) this.$emit("dirty-change", n > 0);
    },
  },

  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    if (this.dirtyCount > 0) this.$emit("dirty-change", false);
  },

  methods: {
    /* ===== 狀態查詢 ===== */
    savedBlocked(date, slot) {
      if (this.fullDaySet.has(date)) return true;
      const set = this.savedSlotsByDate[date];
      return !!(set && set.has(slot));
    },

    isBlocked(date, slot) {
      const key = `${date}|${slot}`;
      return key in this.draft ? this.draft[key] : this.savedBlocked(date, slot);
    },

    isChanged(date, slot) {
      return `${date}|${slot}` in this.draft;
    },

    bookedNames(date, slot) {
      return this.bookedMap[`${date}|${slot}`] || [];
    },

    isDayFullyBlocked(date) {
      return (
        this.allTimeSlots.length > 0 &&
        this.allTimeSlots.every((slot) => this.isBlocked(date, slot))
      );
    },

    /* ===== 顯示 ===== */
    shortLabel(slot) {
      const m = String(slot).match(/^(\d{1,2}):\d{2}/);
      return m ? pad2(m[1]) : slot;
    },

    slotClass(date, slot) {
      const blocked = this.isBlocked(date, slot);
      const booked = this.bookedNames(date, slot).length > 0;
      return {
        open: !blocked && !booked,
        blocked: blocked && !booked,
        booked: booked && !blocked,
        conflict: booked && blocked,
        changed: this.isChanged(date, slot),
      };
    },

    slotStateText(date, slot) {
      const names = this.bookedNames(date, slot);
      if (names.length) {
        return names.length > 1 ? `約 ${names[0]} 等${names.length}人` : `約 ${names[0]}`;
      }
      return this.isBlocked(date, slot) ? "休" : "開";
    },

    slotTitle(date, slot) {
      const lines = [`${date} ${slot}`];
      lines.push(this.isBlocked(date, slot) ? "狀態：不開放" : "狀態：開放");
      const names = this.bookedNames(date, slot);
      if (names.length) lines.push(`已預約：${names.join("、")}`);
      if (this.isChanged(date, slot)) lines.push("（尚未儲存）");
      return lines.join("\n");
    },

    /* ===== 編輯 ===== */
    setBlocked(date, slot, value) {
      const key = `${date}|${slot}`;
      const next = { ...this.draft };
      if (value === this.savedBlocked(date, slot)) delete next[key];
      else next[key] = value;
      this.draft = next;
    },

    toggleSlot(date, slot) {
      if (date < this.todayStr) return;
      const toBlocked = !this.isBlocked(date, slot);
      const names = this.bookedNames(date, slot);
      if (toBlocked && names.length) {
        const ok = confirm(
          `${date} ${slot} 已有預約（${names.join("、")}）。\n設為不開放不會取消既有預約，確定？`,
        );
        if (!ok) return;
      }
      this.setBlocked(date, slot, toBlocked);
    },

    toggleDay(date) {
      if (date < this.todayStr) return;
      const toBlocked = this.allTimeSlots.some((s) => !this.isBlocked(date, s));
      if (toBlocked) {
        const bookedSlots = this.allTimeSlots.filter(
          (s) => !this.isBlocked(date, s) && this.bookedNames(date, s).length,
        );
        if (bookedSlots.length) {
          const who = bookedSlots
            .map((s) => `${this.shortLabel(s)}點 ${this.bookedNames(date, s).join("、")}`)
            .join("\n");
          const ok = confirm(
            `${date} 有預約：\n${who}\n\n整天設為不開放不會取消既有預約，確定？`,
          );
          if (!ok) return;
        }
      }
      this.allTimeSlots.forEach((s) => this.setBlocked(date, s, toBlocked));
    },

    discard() {
      if (this.dirtyCount === 0) return;
      if (!confirm(`要放棄 ${this.dirtyCount} 格未儲存的變更嗎？`)) return;
      this.draft = {};
    },

    /* ===== 儲存：只重算有改過的日期，其他日期原封不動 ===== */
    save() {
      if (this.dirtyCount === 0) return;

      const changedDates = new Set(
        Object.keys(this.draft).map((k) => k.split("|")[0]),
      );
      const known = new Set(this.allTimeSlots);
      const fullDay = new Set(this.fullDaySet);
      const slotsMap = {};
      (Array.isArray(this.unavailable.slots) ? this.unavailable.slots : [])
        .filter((s) => s && s.date)
        .forEach((s) => {
          slotsMap[s.date] = Array.isArray(s.timeSlots) ? [...s.timeSlots] : [];
        });

      changedDates.forEach((date) => {
        const blockedKnown = this.allTimeSlots.filter((s) =>
          this.isBlocked(date, s),
        );
        if (blockedKnown.length === this.allTimeSlots.length) {
          fullDay.add(date);
          delete slotsMap[date];
          return;
        }
        fullDay.delete(date);
        // 保留不在目前時段清單裡的舊格式字串（例如舊的「19:00-20:00(線上)」）
        const legacy = (slotsMap[date] || []).filter((t) => !known.has(t));
        const merged = [...blockedKnown, ...legacy];
        if (merged.length) slotsMap[date] = merged;
        else delete slotsMap[date];
      });

      const updated = {
        fullDay: Array.from(fullDay).sort(),
        slots: Object.keys(slotsMap)
          .sort()
          .map((date) => ({ date, timeSlots: slotsMap[date] })),
      };

      this.$emit("save", updated);
    },

    /* ===== 月份切換（草稿保留） ===== */
    prevMonth() {
      if (this.month === 1) {
        this.month = 12;
        this.year -= 1;
      } else {
        this.month -= 1;
      }
    },
    nextMonth() {
      if (this.month === 12) {
        this.month = 1;
        this.year += 1;
      } else {
        this.month += 1;
      }
    },
    goToday() {
      this.todayStr = taipeiTodayStr();
      this.year = Number(this.todayStr.slice(0, 4));
      this.month = Number(this.todayStr.slice(5, 7));
    },

    onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = "";
    },
  },
};
</script>

<style scoped>
.ucp {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ===== 工具列 ===== */
.ucp-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
}
.ucp-month {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ucp-month-label {
  font-weight: 600;
  font-size: 16px;
  min-width: 120px;
  text-align: center;
}
.ucp-nav,
.ucp-today {
  border: 1px solid #ccc;
  background: #fafafa;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}
.ucp-nav {
  font-size: 18px;
  line-height: 1;
}
.ucp-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ucp-dirty {
  font-size: 13px;
  color: #999;
}
.ucp-dirty.active {
  color: #b26a00;
  font-weight: 600;
}
.ucp-btn {
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
}
.ucp-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.ucp-btn.primary {
  background: #333;
  color: #fff;
}
.ucp-btn.secondary {
  background: #fff;
  color: #555;
  border-color: #ccc;
}

.ucp-hint {
  margin: 0;
  font-size: 13px;
  color: #777;
}

/* ===== 圖例 ===== */
.ucp-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 12px;
  color: #555;
}
.ucp-legend span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1px solid transparent;
}
.dot.open {
  background: #e8f6ec;
  border-color: #7cc28f;
}
.dot.blocked {
  background: #fdecec;
  border-color: #e08a8a;
}
.dot.booked {
  background: #fdf4dc;
  border-color: #d4a73a;
}
.dot.changed {
  background: #fff;
  border: 2px dashed #b26a00;
}

/* ===== 月曆 ===== */
.ucp-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.ucp-weekday {
  text-align: center;
  font-size: 13px;
  color: #666;
  padding: 4px 0;
}
.ucp-cell {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 6px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.ucp-blank {
  border: none;
  background: transparent;
}
.ucp-cell.is-today {
  border-color: #333;
  box-shadow: 0 0 0 1px #333 inset;
}
.ucp-cell.is-past {
  background: #f5f5f5;
  opacity: 0.55;
}

.ucp-date {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px;
  border: none;
  background: transparent;
  padding: 2px 2px 4px;
  cursor: pointer;
  text-align: left;
  border-radius: 4px;
}
.ucp-date:hover:not(:disabled) {
  background: #f0f0f0;
}
.ucp-date:disabled {
  cursor: default;
}
.ucp-date-num {
  font-weight: 700;
  font-size: 15px;
  color: #222;
}
.ucp-date-wd {
  display: none; /* 桌機已有星期表頭；手機版才顯示 */
  font-size: 12px;
  color: #777;
}
.ucp-day-tag {
  margin-left: auto;
  font-size: 11px;
  color: #c0392b;
  background: #fdecec;
  border-radius: 4px;
  padding: 0 4px;
}

.ucp-slots {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ucp-slot {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 5px;
  padding: 2px 5px;
  font-size: 12px;
  cursor: pointer;
  text-align: left;
  min-width: 0;
}
.ucp-slot:disabled {
  cursor: default;
}
.ucp-slot-time {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.ucp-slot-state {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ucp-slot.open {
  background: #e8f6ec;
  border-color: #b9e1c4;
  color: #256b39;
}
.ucp-slot.blocked {
  background: #fdecec;
  border-color: #f0b8b8;
  color: #a33;
}
.ucp-slot.booked {
  background: #fdf4dc;
  border-color: #ecd08a;
  color: #7a5a10;
}
.ucp-slot.conflict {
  background: #fdf4dc;
  border-color: #c0392b;
  color: #a33;
}
.ucp-slot.changed {
  border-style: dashed;
  border-width: 2px;
  border-color: #b26a00;
}
.ucp-slot:not(:disabled):hover {
  filter: brightness(0.96);
}

/* ===== 除錯區 ===== */
.ucp-debug {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}
.ucp-debug pre {
  max-height: 260px;
  overflow: auto;
  background: #f7f7f7;
  padding: 8px;
  border-radius: 6px;
  font-size: 12px;
}

/* ===== 手機：每天一列 ===== */
@media (max-width: 700px) {
  .ucp-grid {
    grid-template-columns: 1fr;
  }
  .ucp-weekday,
  .ucp-blank {
    display: none;
  }
  .ucp-cell {
    flex-direction: row;
    align-items: center;
  }
  .ucp-date {
    width: 74px;
    flex-shrink: 0;
  }
  .ucp-date-wd {
    display: inline;
  }
  .ucp-day-tag {
    margin-left: 0;
  }
  .ucp-slots {
    flex-direction: row;
    flex: 1;
    gap: 4px;
  }
  .ucp-slot {
    flex: 1;
    flex-direction: column;
    align-items: center;
    padding: 4px 2px;
    text-align: center;
  }
  .ucp-slot-state {
    max-width: 100%;
    font-size: 11px;
  }
}
</style>
